"use client";

import * as logger from "@medimo/monitor/logger";
import { SubscriptionActiveProvider } from "@medimo/subscription";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import type { PropsWithChildren } from "react";
import { isAuthProtectedPathname } from "./auth-page";
import { GlobalRecordingProviders } from "./global-recording-providers";
import { NOT_AFFILIATED_PATH, isNotAffiliatedApiError } from "./membership";
import { TechTouch } from "./tech-touch";

// 所属ゲート（MED-188）の 403 NOT_AFFILIATED を受けたら、空白画面やログアウトループに
// せず所属確定フローの案内ページへ誘導する。log-only 期間中は発火しない（enforce 化後に効く）。
const handleNotAffiliatedError = () => {
  if (window.location.pathname === NOT_AFFILIATED_PATH) {
    return;
  }
  if (!isAuthProtectedPathname(window.location.pathname)) {
    return;
  }
  window.location.replace(NOT_AFFILIATED_PATH);
};

let redirectTimer: NodeJS.Timeout | null = null;
let redirectPromise: Promise<void> | null = null;

const createDebouncedRedirectPromise = (
  queryClient: QueryClient,
): Promise<void> => {
  return new Promise<void>((resolve) => {
    redirectTimer = setTimeout(async () => {
      try {
        await queryClient.cancelQueries();
        queryClient.clear();

        window.location.replace("/auth/sign-in");
      } catch (error) {
        logger.error("Failed to redirect to sign-in page", { error });
      } finally {
        redirectTimer = null;
        redirectPromise = null;
        resolve();
      }
    }, 100); // 100ms のデバウンス
  });
};

const handleAuthError = (queryClient: QueryClient) => {
  if (!isAuthProtectedPathname(window.location.pathname)) {
    return;
  }

  if (redirectPromise) {
    return redirectPromise;
  }

  if (redirectTimer) {
    clearTimeout(redirectTimer);
    redirectTimer = null;
  }

  redirectPromise = createDebouncedRedirectPromise(queryClient);
  return redirectPromise;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60, // 1 hour
      staleTime: 1000 * 30, // 30 seconds
      retry: (failureCount, error) => {
        if (
          error instanceof Error &&
          error.message.includes("認証情報の取得に失敗しました")
        ) {
          return false;
        }
        return failureCount < 3;
      },
    },
    mutations: {
      retry: (failureCount, error) => {
        if (
          error instanceof Error &&
          error.message.includes("認証情報の取得に失敗しました")
        ) {
          return false;
        }
        return failureCount < 3;
      },
    },
  },
  queryCache: new QueryCache({
    onError: async (error, { state, ...query }) => {
      const { data, ..._state } = state;
      logger.error(`query:error:${query.options.queryKey}`, {
        error,
        query,
        state: _state,
      });

      if (isNotAffiliatedApiError(error)) {
        handleNotAffiliatedError();
        return;
      }

      if (
        error instanceof Error &&
        error.message.includes("認証情報の取得に失敗しました")
      ) {
        await handleAuthError(queryClient);
      }
    },
    onSuccess(_, { state, ...query }) {
      const { data, ..._state } = state;
      logger.info(`query:success:${query.options.queryKey}`, {
        query,
        state: _state,
      });
    },
  }),
  mutationCache: new MutationCache({
    onMutate(_variables, mutation) {
      const { state, ..._mutation } = mutation;
      const { data, ..._state } = state;
      logger.info(`mutation:${mutation.options.mutationKey}`, {
        mutation: _mutation,
        state: _state,
      });
    },
    onError: async (error, _variables, context, mutation) => {
      const { state, ..._mutation } = mutation;
      const { data, ..._state } = state;
      logger.error(`mutation:error:${mutation.options.mutationKey}`, {
        error,
        context,
        mutation: _mutation,
        state: _state,
      });

      if (isNotAffiliatedApiError(error)) {
        handleNotAffiliatedError();
        return;
      }

      if (
        error instanceof Error &&
        error.message.includes("認証情報の取得に失敗しました")
      ) {
        await handleAuthError(queryClient);
      }
    },
    onSuccess(_, _variables, context, mutation) {
      const { state, ..._mutation } = mutation;
      const { data, ..._state } = state;
      logger.info(`mutation:success:${mutation.options.mutationKey}`, {
        context,
        mutation: _mutation,
        state: _state,
      });
    },
  }),
});

export function Providers({ children }: PropsWithChildren) {
  return (
    <QueryClientProvider client={queryClient}>
      <SubscriptionActiveProvider>
        <TechTouch />
        <GlobalRecordingProviders>{children}</GlobalRecordingProviders>
        <ReactQueryDevtools initialIsOpen={false} />
      </SubscriptionActiveProvider>
    </QueryClientProvider>
  );
}
